import { FC, Fragment, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";
import { Topbar } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { HistoryClock, ScheduleReportGreyIcon } from "assets/icons";
import { TopbarLinkText } from "./ReportsTopbar.styled";
import { useAppSelector, useAppDispatch } from "hooks";
import { selectAccountFeaturesState } from "app/data/selectors";
import { resetReports } from "app/features";
import { compact } from "lodash";

interface Props {
  dropdownTitle: string;
  disabled?: boolean;
  hideScheduleReports?: boolean;
  hideReportHistory?: boolean;
}

const ReportsTopbar: FC<Props> = ({
  dropdownTitle,
  disabled = false,
  hideScheduleReports = false,
  hideReportHistory = false
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { features } = useAppSelector(selectAccountFeaturesState);

  const reportsDropdown = useMemo(
    () =>
      compact([
        {
          name: t("reports.activityReport"),
          onClick: () => {
            dispatch(resetReports());
            navigate("/dashboard/reports/activity-reports");
          }
        },
        {
          name: t("reports.tagPosition"),
          onClick: () => {
            dispatch(resetReports());
            navigate("/dashboard/reports/tag-position");
          }
        },
        features.isRidesEnabled
          ? {
              name: t("reports.mileageRegistration"),
              onClick: () => {
                dispatch(resetReports());
                navigate("/dashboard/reports/mileage-registration");
              }
            }
          : null,
        {
          name: t("reports.workingHours"),
          onClick: () => {
            dispatch(resetReports());
            navigate("/dashboard/reports/working-hours");
          }
        },
        features.isRidesEnabled
          ? {
              name: t("reports.graphicalRides"),
              onClick: () => {
                dispatch(resetReports());
                navigate("/dashboard/reports/graphical-rides");
              }
            }
          : null,
        {
          name: t("reports.graphicalWorkingHours"),
          onClick: () => {
            dispatch(resetReports());
            navigate("/dashboard/reports/graphical-working-hours");
          }
        },
        {
          name: t("reports.co2Report"),
          onClick: () => {
            dispatch(resetReports());
            navigate("/dashboard/reports/co2-reports");
          }
        },
        {
          name: t("reports.multipleRides"),
          onClick: () => {
            dispatch(resetReports());
            navigate("/dashboard/reports/multiple-rides");
          }
        },
        {
          name: t("reports.totalCo2Report"),
          onClick: () => {
            dispatch(resetReports());
            navigate("/dashboard/reports/total-co2-reports");
          }
        }
      ]),
    [t, features, dispatch]
  );

  return (
    <Topbar
      theme={theme}
      dropdownTitle={dropdownTitle}
      dropdownItems={reportsDropdown}
      disabled={disabled}
      increasedDropdownWidth={true}
      breadcrumbTitle="Reports"
      breadcrumbRedirectTo={() => navigate("/dashboard/reports")}
    >
      {features.isAutomaticReportsEnabled && (
        <Fragment>
          <Stack gap={4} direction="row">
            {!hideScheduleReports && (
              <TopbarLinkText>
                <ScheduleReportGreyIcon />
                <Link
                  id="link-schedule-reports"
                  to="/dashboard/reports/schedule-reports"
                >
                  {t("reports.scheduledReports")}
                </Link>
              </TopbarLinkText>
            )}
            {!hideReportHistory && (
              <TopbarLinkText>
                <HistoryClock />
                <Link
                  id="link-reports-history"
                  to="/dashboard/reports/exported-history"
                >
                  {t("reports.reportsHistory")}
                </Link>
              </TopbarLinkText>
            )}
          </Stack>
        </Fragment>
      )}
    </Topbar>
  );
};

export default ReportsTopbar;
